import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { NumericArrayParam, StringParam, useQueryParam, withDefault } from 'use-query-params';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, Typography } from '@material-ui/core';
import { Close, ExpandMore, FilterList } from '@material-ui/icons';
import { ConfigFieldControl, ConfigTextField, SearchBox, useFromLocalCache, useLocalCache } from '@sierralabs/core-ui';
import { useQueryContext } from '@sierralabs/query-ui';
import QueryReportFilterDrawer from '../../common/drawer/QueryReportFilterDrawer';
import SiteQueryPicker from '../../common/pickers/SiteQueryPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useFindNearestRouteComponent } from '../../hooks/site-route/useFindNearestRouteComponent';
import { getRouteLocalCacheKey, useRouteLocalCacheKey } from '../../hooks/site-route/useRouteLocalCacheKey';
import { useMultipleReports } from '../../reports/hooks/useMultipleReports';
// clone optional array
function cloneArray(value) {
    return (value && Array.from(value));
}
function cloneExpressionMap(expressionMap) {
    return Object.assign(Object.assign({}, expressionMap), { selects: cloneArray(expressionMap.selects), wheres: cloneArray(expressionMap.wheres), orderBys: cloneArray(expressionMap.orderBys), groupBys: cloneArray(expressionMap.groupBys), joinAttributes: cloneArray(expressionMap.joinAttributes) });
}
function getDefaultQuery(query) {
    // make sure query is configured first
    if (!(query === null || query === void 0 ? void 0 : query.id)) {
        return;
    }
    // clone expressionMap arrays to avoid being
    // directly modified by SelectQueryBuilder2
    return Object.assign(Object.assign({}, query), { config: query.config
            ? cloneExpressionMap(query.config)
            : { queryType: 'select' } });
}
function useQueryAliases(component) {
    var _a, _b, _c, _d;
    const { queries } = useQueryContext();
    const aliases = [];
    for (const queryId of component.queryIds || []) {
        const query = queries.find(query => query.id === queryId);
        const aliasName = (_b = (_a = query === null || query === void 0 ? void 0 : query.config) === null || _a === void 0 ? void 0 : _a.mainAlias) === null || _b === void 0 ? void 0 : _b.name;
        if (query && aliasName) {
            const queryConfig = (_d = (_c = component.props) === null || _c === void 0 ? void 0 : _c.queries) === null || _d === void 0 ? void 0 : _d.find(queryConfig => queryConfig.id === queryId);
            aliases.push({ name: aliasName, label: queryConfig === null || queryConfig === void 0 ? void 0 : queryConfig.name, query });
        }
    }
    const key = aliases
        .map(alias => `${alias.name}-${alias.label}-${alias.query.id}`)
        .join(',');
    // re-evaluate only when name, label, and query id changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => aliases, [key]);
}
export const SiteSearch = props => {
    // persist selected report on page refresh
    const [reportIds, setReportIds] = useQueryParam('reportIds', withDefault(NumericArrayParam, []));
    const [search, setSearch] = useQueryParam('search', withDefault(StringParam, ''));
    const [openDrawer, setOpenDrawer] = useState(false);
    // temporary selected report ids (before "Apply Report")
    const [selectedReportIds, setSelectedReportIds] = useState(reportIds);
    const component = useSubscribeComponentChanges(SiteSearchPlugin, props);
    const { queries } = useQueryContext();
    const queryIds = useMemo(() => {
        return Array.from(component.queryIds || []);
        // re-evaluate only when component queryIds change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(component.queryIds || []).join(',')]);
    const [queryId, setQueryId] = useState(queryIds[0]);
    const { manager, getReportList, getSelectedReport } = useMultipleReports();
    const localCache = useFromLocalCache();
    const aliases = useQueryAliases(component);
    const route = useFindNearestRouteComponent(component);
    const localCacheKey = useRouteLocalCacheKey(component, { queryId });
    const configuredQuery = queries.find(query => query.id === queryId);
    const { value: query, set: setQuery, refresh: refreshQuery } = useLocalCache(localCacheKey, () => getDefaultQuery(configuredQuery));
    // checks all component queries if they have
    // cached filters and compare to their default values
    const changed = useMemo(() => {
        return queryIds.some(queryId => {
            const key = getRouteLocalCacheKey(route, { queryId });
            const query = queries.find(query => query.id === queryId);
            const defaultQuery = getDefaultQuery(query);
            // compare query configs only
            const value = localCache.get(key);
            return !localCache.compare(value === null || value === void 0 ? void 0 : value.config, defaultQuery === null || defaultQuery === void 0 ? void 0 : defaultQuery.config);
        });
    }, [localCache, queries, queryIds, route]);
    // clear all cached query filters
    const resetQueries = (ids = queryIds) => {
        for (const queryId of ids) {
            const key = getRouteLocalCacheKey(route, { queryId });
            localCache.reset(key);
        }
    };
    const loadReportLists = useCallback((search = '') => {
        aliases.forEach((alias, index) => {
            manager.load(index, { aliasName: alias.name, search });
        });
    }, [aliases, manager]);
    // for cases when configuredQuery is not yet fetched when
    // plugin is rendered, refresh query to update the state value
    // when configuredQuery is finally fetched and found
    useEffect(() => {
        (configuredQuery === null || configuredQuery === void 0 ? void 0 : configuredQuery.id) && refreshQuery();
    }, [configuredQuery === null || configuredQuery === void 0 ? void 0 : configuredQuery.id, refreshQuery]);
    useEffect(() => {
        loadReportLists();
    }, [loadReportLists]);
    useEffect(() => {
        reportIds.forEach((reportId, index) => {
            reportId ? manager.select(index, reportId) : manager.deselect(index);
        });
    }, [manager, reportIds]);
    return (React.createElement(SearchBox, { value: search, onSearch: setSearch },
        React.createElement(Box, { ml: 1 },
            React.createElement(IconButton, { color: 'inherit', onClick: () => setOpenDrawer(true) },
                React.createElement(FilterList, null))),
        changed && (React.createElement(Box, { ml: 1, alignSelf: 'center' },
            React.createElement(Button, { color: 'secondary', variant: 'outlined', endIcon: React.createElement(Close, null), onClick: () => {
                    resetQueries();
                    setReportIds(undefined);
                    setSelectedReportIds([]);
                    manager.clearSelected();
                } }, "Clear Filters"))),
        React.createElement(QueryReportFilterDrawer, { query: query, manager: manager, open: openDrawer, aliases: aliases, selectedReportIds: reportIds, getReportList: getReportList, getSelectedReport: getSelectedReport, onCancel: () => setOpenDrawer(false), onSearch: loadReportLists, onSelectQuery: query => setQueryId(query === null || query === void 0 ? void 0 : query.id), onSelectReportIds: (ids, applyChanges) => {
                // no reports (length 0) or no selected reports
                if (ids.every(id => !id)) {
                    setReportIds(undefined);
                }
                // if report was unset, reset query
                const queryIds = aliases
                    .filter((_, index) => selectedReportIds[index] && !ids[index])
                    .map(alias => alias.query.id);
                resetQueries(queryIds);
                setSelectedReportIds(ids);
                applyChanges && setReportIds(ids);
            }, onQueryChange: query => {
                setOpenDrawer(false);
                setQuery(Object.assign({}, query));
                // NOTE: use empty string instead of null so that
                // the state shows up as null instead of NaN when parsed
                setReportIds(selectedReportIds.every(id => !id) // no selected
                    ? undefined
                    : selectedReportIds.map(id => id || ''));
            } })));
};
const StyledAccordion = styled(Accordion) `
  margin: 0 -12px !important;
  .MuiAccordionSummary-root {
    max-height: 50px;
    padding-left: 12px;
    padding-right: 12px;
    &.Mui-expanded {
      min-height: unset;
    }
    .summary-text {
      width: 0;
      min-width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
  .MuiAccordionDetails-root {
    flex-direction: column;
    padding-left: 12px;
    padding-right: 12px;
    padding-bottom: 12px;
  }
`;
export const SiteSearchConfigurator = (props) => {
    const { component, update } = useConfigComponent(props.component);
    const { queries } = useQueryContext();
    const [expanded, setExpanded] = useState();
    const queryIds = component.queryIds || [];
    const cProps = component.props || {};
    const queryConfigs = cProps.queries || [];
    const getQueryConfigs = (queries) => {
        return queries.map(query => {
            var _a;
            const queryConfig = (_a = cProps.queries) === null || _a === void 0 ? void 0 : _a.find(queryConfig => queryConfig.id === query.id);
            // create new config if it does not exist yet
            return queryConfig || { id: query.id, name: query.name };
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Queries to Filter")),
        React.createElement(ConfigFieldControl, null,
            React.createElement(SiteQueryPicker, { label: 'Query', value: queryIds, onChange: queries => {
                    update('queryIds', queries.map(query => query.id));
                    // update props.queries
                    update('props.queries', getQueryConfigs(queries));
                } })),
        queryConfigs.length > 0 ? (React.createElement(Box, { my: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Queries"),
            React.createElement(Typography, { variant: 'body1', color: 'textSecondary' }, "The Query configuration below will only take effect if there are 2 or more selected Queries."))) : (queryIds.length > 0 && (React.createElement(Button, { fullWidth: true, size: 'small', color: 'primary', variant: 'outlined', onClick: () => {
                // refresh props.queries based on queryIds
                const allQueries = queryIds
                    .map(queryId => queries.find(query => query.id === queryId))
                    .filter((query) => !!query);
                update('props.queries', getQueryConfigs(allQueries));
            } }, "Configure Queries"))),
        queryConfigs.map((queryConfig, index) => {
            const queryPath = `props.queries[${index}]`;
            const query = queries.find(query => query.id === queryConfig.id);
            const title = (queryConfig.name || '?') +
                (query && query.name !== queryConfig.name ? ` (${query.name})` : '');
            return (React.createElement(StyledAccordion, { key: queryConfig.id, expanded: queryConfig.id === expanded, onChange: (_, expanded) => {
                    setExpanded(expanded ? queryConfig.id : undefined);
                } },
                React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMore, null) },
                    React.createElement(Typography, { title: title, className: 'summary-text' }, queryConfig.name || '?')),
                React.createElement(AccordionDetails, null,
                    query && (React.createElement(Typography, { variant: 'body1', color: 'textSecondary' },
                        "Query: ",
                        React.createElement("strong", null, query.name))),
                    React.createElement(ConfigFieldControl, null,
                        React.createElement(ConfigTextField, { fullWidth: true, label: 'Name', value: queryConfig.name || '', onChange: event => {
                                update(`${queryPath}.name`, event.target.value);
                            } })))));
        })));
};
export const SiteSearchPlugin = {
    name: 'Site Search',
    type: 'site-search',
    component: SiteSearch,
    configurator: SiteSearchConfigurator
};
//# sourceMappingURL=SiteSearchPlugin.js.map